<template>
  <div>
    <div class="space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">commute</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Vehicle Types</h2>
          <p class="text-sm text-gray-500">Manage vehicle categories.</p>
        </div>
      </div>
      <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div class="flex items-center space-x-1 text-gray-500">
            <span class="text-base material-icons-round">local_taxi</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Vehicle Types</span>
          </div>
          <div v-if="!adjustingOrder" class="flex flex-wrap items-center gap-2">
            <button
              class="inline-flex items-center h-10 px-4 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm transition-colors duration-150 hover:bg-gray-100"
              @click="adjustingOrder = true"
            >
              <span class="mr-1 text-base material-icons-round">swap_vert</span>
              Adjust Ordering
            </button>
            <router-link
              to="/vehicle-types/add"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white rounded-xl shadow-sm bg-taxiye-500 transition-colors duration-150 hover:bg-taxiye-600"
            >
              <span class="mr-1 text-base material-icons-round">add</span>
              Add Vehicle Type
            </router-link>
          </div>
          <div v-else>
            <button
              class="inline-flex items-center h-10 px-4 text-sm font-semibold text-white bg-green-500 rounded-xl shadow-sm transition-colors duration-150 hover:bg-green-600"
              @click="applyOrder"
            >
              <span class="mr-1 text-base material-icons-round">check</span>
              Done Sorting
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4 overflow-hidden bg-white border border-gray-200 rounded-xl shadow-sm">
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead>
              <tr class="bg-gray-100 border-b border-gray-200">
                <th
                  v-if="adjustingOrder"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  Order
                </th>
                <th
                  v-for="col in [
                    'Name',
                    'Image',
                    'Base Fare',
                    'Price Per KM',
                    'Price Per MIN',
                    'Pool Base Fare',
                    'Pool Price Per KM',
                    'Pool Price Per MIN',
                    'Seats',
                    'City',
                    '',
                  ]"
                  :key="`col-${col}`"
                  class="px-5 py-3 text-xs font-semibold tracking-wider text-left text-gray-500 uppercase whitespace-no-wrap"
                >
                  {{ col }}
                </th>
              </tr>
            </thead>
            <tbody class="relative" v-if="vehicleTypes.length">
              <tr
                v-for="(vehicleType, index) in vehicleTypes"
                :key="vehicleType._id"
                class="transition-colors duration-150 hover:bg-taxiye-50"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-100'"
              >
                <!-- Order -->
                <td v-if="adjustingOrder" class="px-5 py-4 whitespace-no-wrap">
                  <span class="flex items-center justify-center w-8 h-8 text-xs font-bold text-gray-500 rounded-full bg-gray-100">
                    {{ index + 1 }}
                  </span>
                </td>
                <!-- Name -->
                <td class="px-5 py-4 font-medium text-gray-900 whitespace-no-wrap">
                  {{ vehicleType.name }}
                </td>
                <!-- Image -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <img
                    v-if="vehicleType.photo"
                    class="object-cover w-16 h-16 rounded-xl"
                    :src="vehicleType.photo"
                    :alt="vehicleType.name"
                  />
                  <span
                    v-else
                    class="flex items-center justify-center w-16 h-16 text-gray-400 rounded-xl bg-gray-100"
                  >
                    <i class="text-2xl material-icons-round">directions_car</i>
                  </span>
                </td>
                <!-- Base fare -->
                <td class="px-5 py-4 font-medium text-gray-900 whitespace-no-wrap">
                  {{ vehicleType.baseFare || "—" }}
                </td>
                <!-- Price per KM -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ vehicleType.pricePerKM || "0.0" }}
                </td>
                <!-- Price per MIN -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ vehicleType.pricePerMin || "0.0" }}
                </td>
                <!-- Pool base fare -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ vehicleType.poolBaseFare || "—" }}
                </td>
                <!-- Pool price per KM -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ vehicleType.poolPricePerKM || "0.0" }}
                </td>
                <!-- Pool price per MIN -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ vehicleType.poolPricePerMin || "0.0" }}
                </td>
                <!-- Seats -->
                <td class="px-5 py-4 whitespace-no-wrap">
                  <div class="flex items-center space-x-1 text-gray-700">
                    <i class="text-sm text-gray-400 material-icons-round">airline_seat_recline_normal</i>
                    <span>{{ vehicleType.numberOfSeats || "0" }}</span>
                  </div>
                </td>
                <!-- City -->
                <td class="px-5 py-4 text-gray-600 whitespace-no-wrap">
                  {{ vehicleType.city || "—" }}
                </td>
                <!-- Actions -->
                <td
                  class="px-5 py-4 text-right whitespace-no-wrap"
                  v-if="!adjustingOrder"
                >
                  <div class="relative inline-block text-left">
                    <button
                      @click="toggleDropdown(index)"
                      type="button"
                      class="inline-flex items-center justify-center w-9 h-9 text-gray-500 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
                      aria-haspopup="true"
                      :aria-expanded="openDropdown === index"
                      :ref="
                        openDropdown === index
                          ? 'vehicle-type-dropdown-trigger-' + ref
                          : ''
                      "
                    >
                      <i class="material-icons-round">more_vert</i>
                    </button>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0"
                    >
                      <div
                        v-if="openDropdown === index"
                        class="absolute right-0 z-30 w-56 mt-1 origin-top-right bg-white border border-gray-100 shadow-lg rounded-xl"
                        :ref="
                          openDropdown === index
                            ? 'vehicle-type-dropdown-' + ref
                            : ''
                        "
                      >
                        <div
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby="options-menu"
                        >
                          <div class="py-1">
                            <router-link
                              :to="`/vehicle-types/${vehicleType._id}/edit`"
                              class="flex items-center px-4 py-2 space-x-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100"
                              role="menuitem"
                            >
                              <i class="text-base material-icons-round">edit</i>
                              <span>Edit</span>
                            </router-link>
                          </div>
                          <div class="py-1 border-t border-gray-100">
                            <button
                              type="button"
                              @click="openConfirmBox"
                              class="flex items-center w-full px-4 py-2 space-x-2 text-sm text-left text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none"
                              role="menuitem"
                            >
                              <i class="text-base material-icons-round">delete</i>
                              <span>Delete</span>
                              <portal to="modals">
                                <transition
                                  enter-active-class="transition duration-100 ease-out"
                                  enter-class="transform scale-95 opacity-0"
                                  enter-to-class="transform scale-100 opacity-100"
                                  leave-active-class="transition duration-75 ease-in"
                                  leave-class="transform scale-100 opacity-100"
                                  leave-to-class="transform scale-95 opacity-0"
                                >
                                  <div
                                    v-if="confirmOpen"
                                    @click="closeConfirmBox"
                                    class="absolute inset-0 z-40 flex items-center justify-center bg-black bg-opacity-25"
                                  >
                                    <div
                                      @click.stop
                                      class="w-full max-w-lg overflow-hidden bg-white shadow-lg rounded-2xl"
                                    >
                                      <div class="bg-gray-100">
                                        <h3 class="px-6 py-4 font-semibold text-gray-600">
                                          Confirm Delete
                                        </h3>
                                      </div>
                                      <div class="flex items-center px-6 py-6 space-x-4">
                                        <span class="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full bg-red-100">
                                          <i class="text-2xl text-red-500 material-icons-round">warning</i>
                                        </span>
                                        <div>
                                          <p class="text-gray-800">
                                            Are you sure you want to delete this vehicle type?
                                          </p>
                                          <p>
                                            <em class="text-sm text-gray-600">This action can not be undone!</em>
                                          </p>
                                        </div>
                                      </div>
                                      <hr />
                                      <div class="flex items-end justify-between px-6 py-4">
                                        <button
                                          @click="closeConfirmBox"
                                          type="button"
                                          class="px-4 py-2 text-gray-600 rounded-xl transition-colors duration-150 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          @click="remove(index)"
                                          type="button"
                                          :disabled="deleting"
                                          class="flex items-center px-4 py-2 space-x-2 font-semibold border border-transparent shadow-sm rounded-xl"
                                          :class="
                                            deleting
                                              ? 'bg-red-100 text-red-400 cursor-not-allowed'
                                              : 'bg-red-500 text-white hover:bg-red-600'
                                          "
                                        >
                                          <i v-if="deleting" class="material-icons-round spin">sync</i>
                                          <i v-else class="material-icons-round">delete</i>
                                          <span>Delete</span>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </transition>
                              </portal>
                            </button>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </td>
                <td class="px-5 py-4 text-right whitespace-no-wrap" v-else>
                  <div class="inline-flex gap-2">
                    <button
                      class="inline-flex items-center justify-center text-green-600 border border-gray-200 rounded-lg w-9 h-9 transition-colors duration-150 hover:bg-green-50 disabled:opacity-50"
                      v-if="index != 0"
                      title="Move up"
                      @click="moveItem(vehicleType._id, -1)"
                      :disabled="orderingUnderProgress"
                    >
                      <i class="text-base material-icons-round">arrow_upward</i>
                    </button>
                    <button
                      class="inline-flex items-center justify-center text-red-500 border border-gray-200 rounded-lg w-9 h-9 transition-colors duration-150 hover:bg-red-50 disabled:opacity-50"
                      title="Move down"
                      @click="moveItem(vehicleType._id, +1)"
                      :disabled="orderingUnderProgress"
                    >
                      <i class="text-base material-icons-round">arrow_downward</i>
                    </button>
                  </div>
                </td>
              </tr>
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center w-full h-full bg-white bg-opacity-50"
              >
                <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                <span class="invisible block">Loading...</span>
              </div>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="12" class="py-16 text-center bg-white">
                  <div v-if="loading" class="inline-block">
                    <i class="text-5xl text-taxiye-500 material-icons-round spin">sync</i>
                    <span class="invisible block">Loading...</span>
                  </div>
                  <div v-else class="flex flex-col items-center text-gray-400">
                    <span class="flex items-center justify-center w-16 h-16 mb-3 rounded-full bg-gray-100">
                      <i class="text-3xl material-icons-round">local_taxi</i>
                    </span>
                    <span class="text-sm font-medium text-gray-500">No vehicle types found</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex items-end justify-between">
        <pagination
          class="flex space-x-2"
          :currentPage="currentPage"
          :total="total"
          @goto="goToPage"
          @setPerPage="setPerPage"
          :perPage="perPage"
          :start="start"
          :end="end"
          :count="paginator.count"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationMixin from "@/mixins/Pagination";
import Pagination from "@/components/Pagination";

export default {
  mixins: [PaginationMixin],
  data() {
    return {
      vehicleTypes: [],
      openDropdown: false,
      loading: true,
      confirmOpen: false,
      deleting: false,
      adjustingOrder: false,
      orderingUnderProgress: false,
      ref: "",
      perPage: 10,
    };
  },
  components: {
    Pagination,
  },
  created() {
    this.getVehicleTypes();
    this.ref = Math.random().toString(36).substr(2, 5);
  },
  watch: {
    currentPage() {
      this.getVehicleTypes();
    },
    openDropdown(newVal) {
      if (newVal !== false) {
        document.body.addEventListener("click", this.clickAwayHandler);
      } else {
        document.body.removeEventListener("click", this.clickAwayHandler);
      }
    },
  },
  methods: {
    toggleDropdown(index) {
      if (this.openDropdown === index) {
        this.openDropdown = false;
      } else {
        this.openDropdown = index;
      }
    },
    getVehicleTypes() {
      this.loading = true;
      this.paginator = {
        count: 0,
        nextPage: 1,
        prevPage: null,
      };
      return this.$misc
        .get(`vehicleTypes?page=${this.currentPage}`)
        .then((response) => {
          this.vehicleTypes = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count,
          };
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    openConfirmBox() {
      this.confirmOpen = true;
    },
    closeConfirmBox() {
      this.confirmOpen = false;
    },
    remove(index) {
      this.deleting = true;
      this.$misc
        .delete(`/vehicleTypes/${this.vehicleTypes[index]._id}`)
        .then(() => {
          this.vehicleTypes.splice(index, 1);
          window.flash({ type: "success", body: "Vehicle type deleted!" });
          this.openDropdown = false;
        })
        .catch(() => {
          window.flash({
            type: "error",
            body: "Vehicle type couldn't be deleted!",
          });
        })
        .finally(() => {
          this.deleting = false;
          this.openDropdown = false;
          this.confirmOpen = false;
        });
    },
    moveItem(id, direction) {
      this.orderingUnderProgress = true;

      this.$misc
        .post(`/vehicleTypes/adjust-order`, {
          id,
          direction,
        })
        .then(() => {
          return this.getVehicleTypes();
        })
        .catch(() => {
          window.flash({
            type: "error",
            body: "Error while adjusting the sorting order!",
          });
        })
        .finally(() => {
          this.orderingUnderProgress = false;
        });
    },
    applyOrder() {
      this.adjustingOrder = false;
    },
    clickAwayHandler(e) {
      if (
        (this.$refs["vehicle-type-dropdown-trigger-" + this.ref] &&
          this.$refs["vehicle-type-dropdown-trigger-" + this.ref][0].contains(
            e.target
          )) ||
        (this.$refs["vehicle-type-dropdown-" + this.ref] &&
          this.$refs["vehicle-type-dropdown-" + this.ref][0].contains(e.target))
      ) {
        return;
      }

      this.openDropdown = false;
    },
  },
  beforeDestroy() {
    document.body.removeEventListener("click", this.clickAwayHandler);
  },
};
</script>
